import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Loader2, Search, Copy, Check, MessageSquare, Mail, Phone,
  Filter, X, ChevronDown, ChevronUp, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// Channel pill styling
const CHANNEL_META = {
  sms: { label: 'SMS', icon: MessageSquare, badge: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  email: { label: 'Email', icon: Mail, badge: 'bg-blue-100 text-blue-700 border-blue-300' },
  call_script: { label: 'Call Script', icon: Phone, badge: 'bg-violet-100 text-violet-700 border-violet-300' },
};

// Tone pill styling — solid background + dark text works in both modes
const TONE_COLOURS = {
  Friendly: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Urgent: 'bg-orange-100 text-orange-800 border-orange-300',
  Firm: 'bg-rose-100 text-rose-800 border-rose-300',
  Soft: 'bg-sky-100 text-sky-800 border-sky-300',
  'Follow-Up': 'bg-amber-100 text-amber-800 border-amber-300',
};

// Pull all unique tone tags from a list of scripts
const collectTones = (scripts) => {
  const tones = new Set();
  scripts.forEach((s) => (s.tone_tags || []).forEach((t) => tones.add(t)));
  return Array.from(tones).sort();
};

const PreBuiltScripts = () => {
  const { token } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all'); // all | sms | email
  const [toneFilter, setToneFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [collapsed, setCollapsed] = useState({}); // { category: bool }
  const [copiedId, setCopiedId] = useState(null);

  // ── Fetch seeded scripts only ─────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/nurture/templates?is_seed=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json();
        if (!cancelled) setScripts(Array.isArray(d) ? d : []);
      } catch (e) {
        if (!cancelled) toast.error('Could not load scripts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  // ── Derived: filtered scripts grouped by category ─────────────────────
  const { grouped, totalCount, availableTones, availableCategories } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = scripts.filter((s) => {
      if (channelFilter !== 'all' && s.template_type !== channelFilter) return false;
      if (toneFilter !== 'all' && !(s.tone_tags || []).includes(toneFilter)) return false;
      if (categoryFilter !== 'all' && (s.category || 'Uncategorised') !== categoryFilter) return false;
      if (!q) return true;
      const hay = `${s.name} ${s.subject || ''} ${s.body || ''} ${s.category || ''} ${(s.tone_tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
    const groups = {};
    filtered.forEach((s) => {
      const cat = s.category || 'Uncategorised';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    // Stable category order — sort by category name asc
    const orderedKeys = Object.keys(groups).sort();
    const ordered = {};
    orderedKeys.forEach((k) => { ordered[k] = groups[k]; });
    // Categories dropdown ignores the active categoryFilter so the list
    // doesn't collapse to just the picked one — show counts respecting the
    // OTHER filters (channel/tone/search) so the user can see what's
    // available before they switch.
    const catCounts = {};
    scripts.forEach((s) => {
      if (channelFilter !== 'all' && s.template_type !== channelFilter) return;
      if (toneFilter !== 'all' && !(s.tone_tags || []).includes(toneFilter)) return;
      if (q) {
        const hay = `${s.name} ${s.subject || ''} ${s.body || ''} ${s.category || ''} ${(s.tone_tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      const cat = s.category || 'Uncategorised';
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    const categories = Object.entries(catCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return {
      grouped: ordered,
      totalCount: filtered.length,
      availableTones: collectTones(scripts),
      availableCategories: categories,
    };
  }, [scripts, search, channelFilter, toneFilter, categoryFilter]);

  // ── Copy script body to clipboard ─────────────────────────────────────
  // We copy ONLY the customer-facing text so brokers can paste straight
  // into SMS / email without editing. Rules:
  //   • Internal "RULE: ..." coaching notes → dropped entirely.
  //   • The "[Your Name]" placeholder → removed inline (or drops the whole
  //     line if the line was ONLY the placeholder, i.e. a signature line).
  //   • The subject line lives above the body in the UI, so we do not
  //     prepend it to the copied text.
  //   • On non-call scripts, the "IF THEY ANSWER / IF THEY DO NOT ANSWER"
  //     labels shouldn't appear anyway — but if they do, we drop them.
  //   • On CALL scripts, we KEEP the "IF THEY..." labels because the caller
  //     needs them to navigate the two branches — but we still strip
  //     [Your Name] inline so the caller's own name isn't in the copy.
  //   • We collapse the sign-off scaffolding ("Thanks," / "All the best,")
  //     when it was ONLY there to precede [Your Name] so the pasted message
  //     doesn't end with a dangling "Thanks,".
  const copyableText = useCallback((script) => {
    const isCall = script?.template_type === 'call_script';
    const raw = String(script?.body || '');
    const lines = raw.split('\n');
    const kept = [];
    for (const rawLine of lines) {
      let line = rawLine;
      const trimmed = line.trim();
      if (/^RULE:/i.test(trimmed)) continue;
      if (!isCall && /^IF THEY ANSWER\b/i.test(trimmed)) continue;
      if (!isCall && /^IF THEY DO NOT ANSWER\b/i.test(trimmed)) continue;
      // Signature line (only "[Your Name]" possibly with trailing punctuation)
      if (/^\[Your Name\][.,!]*$/i.test(trimmed)) continue;
      // Inline strip: "it's [Your Name] from Derwent" → "it's from Derwent"
      // Preserve punctuation & spacing so the sentence still reads naturally.
      if (/\[Your Name\]/i.test(line)) {
        line = line
          .replace(/\s*\[Your Name\]\s*/gi, ' ')
          .replace(/[ \t]{2,}/g, ' ')
          .replace(/[ \t]+([.,!?])/g, '$1');
      }
      kept.push(line);
    }
    // Drop dangling sign-offs left behind after removing the signature
    while (kept.length && /^(thanks|thank you|cheers|regards|kind regards|all the best|best|speak soon)[,.!]*$/i.test(kept[kept.length - 1].trim())) {
      kept.pop();
    }
    return kept
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }, []);

  const handleCopy = useCallback(async (script) => {
    try {
      const text = copyableText(script);
      await navigator.clipboard.writeText(text);
      setCopiedId(script.id);
      toast.success(`Copied "${script.name}"`);
      setTimeout(() => setCopiedId((c) => (c === script.id ? null : c)), 1800);
    } catch (e) {
      toast.error('Could not copy — paste manually');
    }
  }, [copyableText]);

  const toggleCollapse = (cat) =>
    setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  const collapseAll = () => {
    const all = {};
    Object.keys(grouped).forEach((k) => { all[k] = true; });
    setCollapsed(all);
  };
  const expandAll = () => setCollapsed({});

  const clearFilters = () => {
    setSearch('');
    setChannelFilter('all');
    setToneFilter('all');
    setCategoryFilter('all');
  };

  const filtersActive = search || channelFilter !== 'all' || toneFilter !== 'all' || categoryFilter !== 'all';

  return (
    <div className="min-h-screen flex flex-col bg-page text-text-main pb-24 md:pb-0" data-testid="prebuilt-scripts-page">
      <Header />

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12 flex-1">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold mb-2">
            <FileText className="w-3.5 h-3.5" />
            <span>Lead Nurture · Compliant Communication</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950 dark:text-white mb-2">
            Pre-Built <span className="text-crm-blue">Scripts</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl">
            Ready-to-use SMS, email and call scripts across the full lifecycle.
            Search · filter · copy in one tap. Placeholders like{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[11px]">[First Name]</code>{' '}
            stay intact so you swap names before sending.
          </p>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900/60 border border-surface-border rounded-2xl p-4 sm:p-5 mb-6 shadow-soft">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search scripts by name, body, tone..."
                className="pl-10 dark:bg-slate-800 dark:border-slate-700"
                data-testid="scripts-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  aria-label="Clear search"
                  data-testid="scripts-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Channel pills */}
            <div className="flex items-center gap-1.5" data-testid="scripts-channel-filters">
              {[
                { k: 'all', label: 'All' },
                { k: 'sms', label: 'SMS' },
                { k: 'email', label: 'Email' },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setChannelFilter(k)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all',
                    channelFilter === k
                      ? 'bg-crm-blue text-white border-crm-blue shadow-sm'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-crm-blue/50',
                  )}
                  data-testid={`scripts-channel-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Quick preset — Client Portal Sequence.
                Toggles the categoryFilter to the 2-week chase cadence so
                brokers can pull it up with one tap instead of hunting the
                dropdown. */}
            {(() => {
              const CPS = 'Client Portal Sequence';
              const active = categoryFilter === CPS;
              return (
                <button
                  type="button"
                  onClick={() => setCategoryFilter(active ? 'all' : CPS)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-bold border transition-all inline-flex items-center gap-1.5 whitespace-nowrap',
                    active
                      ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm'
                      : 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100',
                  )}
                  data-testid="scripts-quick-client-portal-sequence"
                  title="Two-week Client Portal chase cadence — Day 1 through Day 14"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Client Portal Sequence
                </button>
              );
            })()}

            {/* Tone select */}
            <div className="relative">
              <select
                value={toneFilter}
                onChange={(e) => setToneFilter(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 rounded-full text-xs font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-crm-blue/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-crm-blue/40"
                data-testid="scripts-tone-filter"
                aria-label="Filter by tone"
              >
                <option value="all">All tones</option>
                {availableTones.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            </div>

            {/* Category select — pick a single lifecycle stage to focus on */}
            <div className="relative">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className={cn(
                  'appearance-none pl-9 pr-8 py-2 rounded-full text-xs font-semibold border cursor-pointer focus:outline-none focus:ring-2 focus:ring-crm-blue/40 max-w-[260px] truncate',
                  categoryFilter === 'all'
                    ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-crm-blue/50'
                    : 'bg-crm-blue text-white border-crm-blue shadow-sm',
                )}
                data-testid="scripts-category-filter"
                aria-label="Filter by category"
                title={categoryFilter === 'all' ? 'All categories' : categoryFilter}
              >
                <option value="all">All categories</option>
                {availableCategories.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </option>
                ))}
              </select>
              <FileText className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none',
                categoryFilter === 'all' ? 'text-slate-400' : 'text-white',
              )} />
              <ChevronDown className={cn(
                'absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none',
                categoryFilter === 'all' ? 'text-slate-400' : 'text-white',
              )} />
            </div>

            {filtersActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="text-xs dark:bg-slate-800 dark:border-slate-700"
                data-testid="scripts-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Result summary + expand/collapse */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
            <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="scripts-result-count">
              <span className="font-bold text-slate-900 dark:text-white">{totalCount}</span>{' '}
              script{totalCount === 1 ? '' : 's'} · {Object.keys(grouped).length} categor{Object.keys(grouped).length === 1 ? 'y' : 'ies'}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={expandAll}
                className="text-xs font-semibold text-crm-blue hover:underline"
                data-testid="scripts-expand-all"
              >
                Expand all
              </button>
              <span className="text-slate-300 dark:text-slate-700">·</span>
              <button
                type="button"
                onClick={collapseAll}
                className="text-xs font-semibold text-crm-blue hover:underline"
                data-testid="scripts-collapse-all"
              >
                Collapse all
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="scripts-loading">
            <Loader2 className="w-7 h-7 animate-spin text-crm-blue" />
          </div>
        ) : totalCount === 0 ? (
          <div className="bg-white dark:bg-slate-900/60 border border-surface-border rounded-2xl p-12 text-center" data-testid="scripts-empty">
            <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
              No scripts match those filters
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Try clearing the search or selecting a different channel/tone.
            </p>
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="scripts-empty-clear">
              <X className="w-4 h-4 mr-1" /> Clear filters
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {Object.entries(grouped).map(([category, items]) => {
              const isCollapsed = !!collapsed[category];
              return (
                <section
                  key={category}
                  className="bg-white dark:bg-slate-900/60 border border-surface-border rounded-2xl overflow-hidden shadow-soft"
                  data-testid={`scripts-category-${category}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleCollapse(category)}
                    className="w-full flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-crm-blue/5 to-transparent hover:from-crm-blue/10 transition-colors text-left"
                    data-testid={`scripts-category-toggle-${category}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-crm-blue text-white flex items-center justify-center font-bold text-xs">
                        {items.length}
                      </div>
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                        {category}
                      </h2>
                    </div>
                    {isCollapsed ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    )}
                  </button>

                  {!isCollapsed && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4 sm:p-5">
                      {items.map((s) => {
                        const meta = CHANNEL_META[s.template_type] || CHANNEL_META.email;
                        const Icon = meta.icon;
                        const isCopied = copiedId === s.id;
                        return (
                          <article
                            key={s.id}
                            className="group relative flex flex-col bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:border-crm-blue/50 hover:shadow-md transition-all"
                            data-testid={`script-card-${s.id}`}
                          >
                            {/* Top: channel + tones */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <span className={cn('inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border', meta.badge)}>
                                <Icon className="w-3 h-3" /> {meta.label}
                              </span>
                              <div className="flex flex-wrap gap-1 justify-end">
                                {(s.tone_tags || []).map((t) => (
                                  <span
                                    key={t}
                                    className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded border', TONE_COLOURS[t] || 'bg-slate-100 text-slate-700 border-slate-300')}
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Title */}
                            <h3 className="font-bold text-sm text-slate-900 dark:text-white leading-snug mb-1">
                              {s.name}
                            </h3>

                            {/* Subject (email only) */}
                            {s.template_type === 'email' && s.subject && (
                              <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1.5 truncate">
                                Subject: <span className="text-slate-700 dark:text-slate-200">{s.subject}</span>
                              </div>
                            )}

                            {/* Body preview */}
                            <p
                              className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap mb-3 flex-1"
                              data-testid={`script-body-${s.id}`}
                            >
                              {s.body}
                            </p>

                            {/* Copy CTA */}
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleCopy(s)}
                              className={cn(
                                'w-full transition-all',
                                isCopied
                                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                  : 'bg-crm-blue hover:opacity-90 text-white',
                              )}
                              data-testid={`script-copy-${s.id}`}
                            >
                              {isCopied ? (
                                <>
                                  <Check className="w-4 h-4 mr-1.5" /> Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4 mr-1.5" /> Copy script
                                </>
                              )}
                            </Button>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreBuiltScripts;
